import { Controller } from "@hotwired/stimulus"
import { loadPlan, replacePlan, savePlan } from "plan_storage"
import { buildTokenizer } from "phi_tokenizer"

// AI proofreading round-trip (built for Claude).
//
//   Export → every narrative string in the plan (EXCEPT the Client, About,
//   and Circle of Support sections, which hold identity/PII) is collected
//   with a stable id + path. All personal identifiers — first name, last
//   name, age, DOB — are replaced with [FirstName] / [LastName] / [Age] /
//   [DOB] tokens BEFORE export, so the file carries NO PHI. The downloaded
//   JSON embeds a self-contained instruction block (role, rules, output
//   format, and a worked example) so the user can paste the WHOLE file into
//   Claude and get back an edited copy of the same file — no prompt to write.
//
//   Import → Claude's reply is parsed (tolerant of ```json code fences and
//   any prose Claude wraps around the JSON) and each item's text is written
//   back to its path (arrays are matched by item id, so reordering is safe).
//   Any leftover //instruction// marker is stripped; [Tokens] are kept as-is
//   and the editor/preview renders them back to the real values.
//
// PHI note: keeping names + age as tokens is the whole point — a token-only
// export is not protected health information, which keeps AI proofreading
// outside HIPAA/PHI handling requirements.

// Sections (top-level plan keys) never exported: identity/PII + non-prose
// utility data (calendar colors, print settings, signatures, meta).
const EXCLUDE_TOP = new Set([
  "version", "id", "meta", "client", "about", "circleOfSupport",
  "resourceCalendar", "review", "facilitator"
])

// Keys whose string values are structural (ids, contacts, enums, numbers,
// colors) — not prose, so they're skipped even inside included sections.
const SKIP_KEYS = new Set([
  "id", "tag", "type", "color", "colorKey", "icon", "iconKey", "source",
  "domain", "category", "regionalCenter", "relationship", "abbrev",
  "vendorId", "vendorName", "vendorPhone", "vendorEmail", "vendorWebsite",
  "vendorAddress", "servicesLocation", "ratio", "cost", "mileage", "rate",
  "dayType", "includeMileageInPdf", "linkedFromWhoLives", "url"
])

// Per-item author instruction: text wrapped in //double slashes// tells the
// AI to apply a specific change (not just proofread). The marker is removed
// on the way back — by the AI, and again here (see _stripInstructions) as a
// safety net so it can never survive into the plan. A "//" is treated as a
// delimiter ONLY when it is not part of a URL scheme's "://", so an
// instruction may freely contain single slashes (he/she, 24/7), whole URLs,
// and line breaks without the marker chewing through them.

// Whole sub-trees to skip (numbers/contacts/flags, no prose).
const SKIP_SUBTREE = new Set([
  "schedule", "vendors", "pageBreaks", "sectionEnabled", "proofread"
])

// localStorage key holding the LAST import's per-field changes so the
// highlights + diff popovers survive a section navigation / reload until the
// user clicks "Clear AI highlights". Kept OUT of the plan so it never rides
// along into an export or an .sdp save.
const HL_KEY = "pcp_ai_highlights"

// ── Character-level diff (drives the AI-change review popover) ──────────────
// Trim the common prefix/suffix, run an LCS on the differing middle, and
// return merged ops [{ t:"eq"|"ins"|"del", s }]. Fast for the localized edits
// proofreading produces; a size guard falls back to a whole-string replace.
function mergeOps(ops) {
  const out = []
  for (const o of ops) {
    const last = out[out.length - 1]
    if (last && last.t === o.t) last.s += o.s
    else out.push({ t: o.t, s: o.s })
  }
  return out
}
function lcsDiff(a, b) {
  const n = a.length, m = b.length
  if (!n && !m) return []
  if (!n) return [{ t: "ins", s: b }]
  if (!m) return [{ t: "del", s: a }]
  if (n * m > 240000) return [{ t: "del", s: a }, { t: "ins", s: b }]
  const dp = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1))
  for (let i = n - 1; i >= 0; i--)
    for (let j = m - 1; j >= 0; j--)
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1])
  const ops = []
  let i = 0, j = 0
  while (i < n && j < m) {
    if (a[i] === b[j]) { ops.push({ t: "eq", s: a[i] }); i++; j++ }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { ops.push({ t: "del", s: a[i] }); i++ }
    else { ops.push({ t: "ins", s: b[j] }); j++ }
  }
  while (i < n) ops.push({ t: "del", s: a[i++] })
  while (j < m) ops.push({ t: "ins", s: b[j++] })
  return ops
}
function diffCharOps(a, b) {
  a = String(a); b = String(b)
  let start = 0
  const al = a.length, bl = b.length, ms = Math.min(al, bl)
  while (start < ms && a[start] === b[start]) start++
  let ae = al, be = bl
  while (ae > start && be > start && a[ae - 1] === b[be - 1]) { ae--; be-- }
  const ops = []
  if (start) ops.push({ t: "eq", s: a.slice(0, start) })
  ops.push(...lcsDiff(a.slice(start, ae), b.slice(start, be)))
  if (ae < al) ops.push({ t: "eq", s: a.slice(ae) })
  return mergeOps(ops)
}
function escapeHtml(s) {
  return String(s).replace(/[&<>"]/g, (c) => (
    c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : "&quot;"
  ))
}
// Unified inline diff HTML: equal text plain, insertions green, deletions red
// strikethrough. Newlines become <br> so multi-line fields read naturally.
function diffHtml(oldText, newText) {
  return diffCharOps(oldText, newText).map((o) => {
    const html = escapeHtml(o.s).replace(/\n/g, "<br>")
    if (o.t === "ins") return `<ins class="en-ai-ins">${html}</ins>`
    if (o.t === "del") return `<del class="en-ai-del">${html}</del>`
    return html
  }).join("")
}

export default class extends Controller {
  static targets = ["fileInput", "clearBtn"]

  connect() {
    // Restore highlights from the last import so they survive a section
    // navigation / page reload until the user clears them.
    this._changes = this._loadHL()
    // Give the plan a stable id the moment the editor loads, so an export
    // always carries one and imports can verify the file belongs to it.
    this._ensureStablePlanId()
    this._onDocClick = (e) => this._onDocClickHandler(e)
    this._onKeyDown  = (e) => { if (e.key === "Escape") this._hidePopover() }
    this._onScroll   = (e) => {
      // Dismiss on PAGE scroll only — never when scrolling INSIDE the popover
      // (its own overflow:auto body fires scroll events too, which used to
      // close the box the moment the user tried to read a long diff).
      const t = e && e.target
      if (this._pop && t && t.nodeType && this._pop.contains(t)) return
      this._hidePopover()
    }
    this._onPlanStar = (e) => this._onWholePlanChange(e)
    document.addEventListener("click", this._onDocClick, true)
    document.addEventListener("keydown", this._onKeyDown)
    window.addEventListener("scroll", this._onScroll, true)
    window.addEventListener("resize", this._onScroll)
    document.addEventListener("plan:change", this._onPlanStar)
    this._reflectClearBtn()
    if (this._changes.length) this._scheduleApply()
  }

  disconnect() {
    document.removeEventListener("click", this._onDocClick, true)
    document.removeEventListener("keydown", this._onKeyDown)
    window.removeEventListener("scroll", this._onScroll, true)
    window.removeEventListener("resize", this._onScroll)
    document.removeEventListener("plan:change", this._onPlanStar)
    if (this._applyRaf) cancelAnimationFrame(this._applyRaf)
    this._hidePopover()
  }

  // ---- Export ----
  exportForAI(event) {
    event?.preventDefault?.()
    const plan = this._load()
    const planId = this._ensurePlanId(plan)   // ties this file to THIS plan
    savePlan(plan)                             // persist the id for future imports
    const client = plan.client || {}
    const age = this._age(client.dob)
    const gender = (client.gender || "").trim()
    const tok = this._tokenizer(plan)
    const items = []
    let n = 0
    const nextId = () => `T${++n}`
    for (const [k, v] of Object.entries(plan)) {
      if (EXCLUDE_TOP.has(k)) continue
      this._walk(v, [k], items, nextId, tok)
    }
    if (!items.length) { this._toast("No proofread-able text found."); return }
    const doc = {
      pcpmakerProofread: 1,
      formatVersion: 3,
      // Identity guard: planId ties the file to one specific plan; the
      // participantHash is a non-reversible fingerprint of the participant's
      // name + DOB (NOT the name itself). On import we compare these against
      // the open plan and warn if the file was made for a DIFFERENT plan /
      // person — so a proofread file can't be applied to the wrong participant.
      planId,
      participantHash: this._participantHash(plan),
      exportedAt: new Date().toISOString(),
      howToUse:
        "HOW TO USE — open Claude (claude.ai or the Claude app), start a new " +
        "chat, paste this ENTIRE file in, and send it. Claude will reply with " +
        "an edited copy of this file. Save that reply as a .json file, then " +
        "click \"Import proofread\" in PCP Maker to apply the changes.",
      aiInstructions: {
        role:
          "You are a meticulous proofreader and copy-editor for Person-Centered " +
          "Plans (PCPs) — supportive documents written about a person who " +
          "receives disability or care services.",
        task:
          "Proofread the \"text\" value of EVERY entry in the \"items\" array " +
          "below (this is the default job for every item), AND wherever an " +
          "item carries a //instruction// also carry out that instruction. Then " +
          "return the whole file.",
        rules: [
          "By DEFAULT, proofread every item's \"text\" for spelling, grammar, punctuation, and clarity while preserving the original meaning, facts, and tone.",
          "In ADDITION, if a \"text\" contains an instruction wrapped in //double slashes// — e.g. //make this more concise// or //rewrite in first person// — carry out that instruction as well, then delete the //...// marker from your result.",
          "Edit ONLY the \"text\" field. Never change, reorder, or remove any \"id\" or \"path\".",
          "Keep every bracketed [Token] — such as [FirstName], [LastName], [Age], [DOB] — EXACTLY as written. Never expand, rename, translate, or delete a token.",
          "Use the participant's age and gender in the \"participant\" block below to choose correct pronouns (she/her, he/him, or they/them) and age-appropriate wording. NEVER copy the age or gender values into any text — keep the [Tokens] as-is.",
          "Do not invent or add information. Improve wording only; never introduce facts that are not already present.",
          "Keep the same items in the same order — do not add, drop, split, or merge items.",
          "Preserve intentional line breaks and list formatting within a text.",
          "Use warm, respectful, person-first language appropriate for a Person-Centered Plan."
        ],
        outputFormat:
          "Reply with ONLY the complete JSON, wrapped in a single ```json code " +
          "block, using the same top-level keys and the same items in the same " +
          "order. Change nothing except the \"text\" values (and removing any " +
          "//...// markers). Do not write any explanation before or after the code block.",
        example: {
          note: "Illustration only — do NOT include this example object in your output.",
          before: { id: "T0", path: ["goals", "g-1", "text"], text: "[FirstName] want to be more independant //make this concise//" },
          after:  { id: "T0", path: ["goals", "g-1", "text"], text: "[FirstName] wants to be more independent." }
        }
      },
      participant: {
        note: "Non-identifying context so you can pick correct pronouns and age-appropriate wording. Do NOT copy these values into any item text.",
        age: (age == null) ? null : (age > 89 ? "90+" : age),
        gender: gender || "unspecified"
      },
      privacyNote:
        "This file contains NO personal information. The participant's name, " +
        "age, and date of birth have been replaced with [FirstName] / " +
        "[LastName] / [Age] / [DOB] tokens before export, so it is safe to " +
        "share with an AI. Keep the tokens intact.",
      items
    }
    this._download(doc)
    this._toast(`Exported ${items.length} text items for Claude — no personal info included.`)
  }

  // Recursively collect narrative strings. Arrays of {id,…} use the item id
  // as the path segment (stable across reorders); string arrays use the index.
  _walk(value, path, items, nextId, tok) {
    if (typeof value === "string") {
      const key = path[path.length - 1]
      if (SKIP_KEYS.has(key)) return
      const t = value.trim()
      if (!t || !/[A-Za-z]/.test(t)) return   // skip empty / pure-number/-symbol
      items.push({ id: nextId(), path: [...path], text: tok(value) })
    } else if (Array.isArray(value)) {
      value.forEach((item, i) => {
        if (item && typeof item === "object") {
          const seg = item.id != null ? String(item.id) : String(i)
          this._walk(item, [...path, seg], items, nextId, tok)
        } else if (typeof item === "string") {
          this._walk(item, [...path, String(i)], items, nextId, tok)
        }
      })
    } else if (value && typeof value === "object") {
      for (const [k, v] of Object.entries(value)) {
        if (SKIP_KEYS.has(k) || SKIP_SUBTREE.has(k)) continue
        this._walk(v, [...path, k], items, nextId, tok)
      }
    }
  }

  // Build a function that strips PHI from a string. Names/age/DOB already
  // stored as [Tokens] are left alone. Delegates to the shared phi_tokenizer
  // module so proofread_bucket can reuse the same logic.
  _tokenizer(plan) {
    return buildTokenizer(plan)
  }

  _age(dob) {
    if (!dob) return null
    const p = String(dob).split("-")
    if (p.length !== 3) return null
    const y = +p[0], m = +p[1] - 1, d = +p[2]
    if (!y || Number.isNaN(m) || !d) return null
    const t = new Date()
    let a = t.getFullYear() - y
    const md = t.getMonth() - m
    if (md < 0 || (md === 0 && t.getDate() < d)) a--
    return a
  }

  _download(doc) {
    // The file's CONTENTS are PHI-free (names tokenized), so keep the
    // FILENAME free of the participant's name too — a name in the download
    // would leak PHI into the Downloads folder / cloud sync. Generic name +
    // date; the browser adds "(1)", "(2)" for same-day repeats.
    const stamp = new Date().toISOString().slice(0, 10)   // YYYY-MM-DD
    const blob = new Blob([JSON.stringify(doc, null, 2)], { type: "application/json" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `pcp-proofread-${stamp}.json`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  // ---- Import ----
  pickFile(event) {
    event?.preventDefault?.()
    if (this.hasFileInputTarget) this.fileInputTarget.click()
  }

  async fileChosen(event) {
    const file = event.currentTarget.files && event.currentTarget.files[0]
    event.currentTarget.value = ""   // allow re-importing the same file
    if (!file) return
    const doc = this._parseDoc(await file.text())
    if (!doc) {
      this._toast("Couldn't read that file — save Claude's reply as .json (or re-export) and try again.")
      return
    }
    this._apply(doc)
  }

  // Parse the imported file into a doc object. Users often save Claude's whole
  // reply, which can wrap the JSON in a ```json code fence or add a sentence
  // around it — so fall back from a strict parse to (1) the first fenced block,
  // then (2) the outermost { … } span, before giving up.
  _parseDoc(raw) {
    const tryParse = (s) => { try { return JSON.parse(s) } catch (_) { return null } }
    let doc = tryParse(raw)
    if (doc) return doc
    const fence = String(raw).match(/```(?:json)?\s*([\s\S]*?)```/i)
    if (fence && (doc = tryParse(fence[1]))) return doc
    const first = String(raw).indexOf("{")
    const last  = String(raw).lastIndexOf("}")
    if (first >= 0 && last > first && (doc = tryParse(raw.slice(first, last + 1)))) return doc
    return null
  }

  _apply(doc) {
    if (!doc || doc.pcpmakerProofread == null || !Array.isArray(doc.items)) {
      this._toast("Not a PCP Maker proofread file.")
      return
    }
    const plan = this._load()
    // Guard: don't apply a proofread file exported from a DIFFERENT plan /
    // participant onto this one.
    if (!this._confirmSamePlan(doc, plan)) { this._toast("Import cancelled."); return }
    const changes = []
    let applied = 0, skipped = 0
    for (const it of doc.items) {
      if (!it || !Array.isArray(it.path) || typeof it.text !== "string") { skipped++; continue }
      const before = this._getPath(plan, it.path)   // capture BEFORE overwrite
      // Safety net: strip any //instruction// marker the AI left behind so it
      // never lands in the plan (collapse the surrounding whitespace too).
      const text = this._stripInstructions(it.text)
      if (this._setPath(plan, it.path, text)) {
        applied++
        if (typeof before === "string" && before !== text) {
          changes.push({ path: it.path, old: before, new: text })
        }
      } else skipped++
    }
    if (!applied) { this._toast(`Nothing to import — ${skipped} item(s) didn't match this plan.`); return }
    replacePlan(plan)
    document.dispatchEvent(new CustomEvent("plan:change", {
      bubbles: true, detail: { path: "*", plan, source: "ai-proofread" }
    }))
    // Remember what changed and highlight it for review (survives navigation
    // until the user clicks "Clear AI highlights").
    this._changes = changes
    this._saveHL(changes)
    this._reflectClearBtn()
    this._scheduleApply()
    const n = changes.length
    this._toast(
      n
        ? `Imported ${applied} update${applied === 1 ? "" : "s"} — ${n} changed field${n === 1 ? "" : "s"} highlighted. Click one to see the change.`
        : `Imported ${applied} update${applied === 1 ? "" : "s"} — no text changed.`
    )
  }

  // Remove every //instruction// marker from a string. A "//" counts as a
  // delimiter ONLY when it is not the "//" of a URL scheme's "://", so an
  // instruction can contain single slashes, whole URLs, and line breaks; an
  // UNCLOSED "//" is left in place (safer to show it than to eat the rest).
  _stripInstructions(text) {
    const spans = this._instructionSpans(String(text))
    if (!spans.length) return String(text)
    let out = String(text)
    for (let k = spans.length - 1; k >= 0; k--) {
      out = out.slice(0, spans[k][0]) + " " + out.slice(spans[k][1])
    }
    return out.replace(/[ \t]{2,}/g, " ").replace(/[ \t]+\n/g, "\n").trim()
  }

  // [start, end) spans of //…// markers. `bare(i)` is true when text[i..i+1]
  // is "//" that is NOT preceded by ":" (so the "//" inside "https://" is
  // never mistaken for a delimiter). Scans left-to-right, non-overlapping.
  _instructionSpans(text) {
    const t = String(text), n = t.length, spans = []
    const bare = (i) => t[i] === "/" && t[i + 1] === "/" && !(i > 0 && t[i - 1] === ":")
    let i = 0
    while (i < n - 1) {
      if (bare(i)) {
        let j = i + 2, close = -1
        while (j < n - 1) { if (bare(j)) { close = j; break } j++ }
        if (close === -1) break            // unclosed → leave the rest intact
        spans.push([i, close + 2])
        i = close + 2
        continue
      }
      i++
    }
    return spans
  }

  // Walk to the parent of the final segment, then set — only when the target
  // already exists (never create keys/rows from an edited file).
  _setPath(plan, path, text) {
    let cur = plan
    for (let i = 0; i < path.length - 1; i++) {
      cur = this._child(cur, String(path[i]))
      if (cur == null || typeof cur !== "object") return false
    }
    return this._setChild(cur, String(path[path.length - 1]), text)
  }

  _child(cur, seg) {
    if (Array.isArray(cur)) {
      const byId = cur.find(it => it && typeof it === "object" && String(it.id) === seg)
      if (byId) return byId
      const idx = Number(seg)
      return Number.isInteger(idx) ? cur[idx] : undefined
    }
    if (cur && typeof cur === "object") return cur[seg]
    return undefined
  }

  _setChild(cur, seg, text) {
    if (Array.isArray(cur)) {
      const idx = Number(seg)
      if (Number.isInteger(idx) && idx >= 0 && idx < cur.length && typeof cur[idx] === "string") {
        cur[idx] = text
        return true
      }
      return false
    }
    if (cur && typeof cur === "object" && Object.prototype.hasOwnProperty.call(cur, seg)) {
      cur[seg] = text
      return true
    }
    return false
  }

  // ---- AI change highlights + diff popover ----

  // Public Stimulus action (Clear AI highlights button): drop all highlights.
  clearHighlights(event) {
    event?.preventDefault?.()
    this._changes = []
    this._saveHL([])
    this._removeMarks()
    this._hidePopover()
    this._reflectClearBtn()
  }

  // Read a string leaf from the plan by the same path segments the import
  // uses (arrays matched by item id via _child). undefined if not a string.
  _getPath(plan, path) {
    let cur = plan
    for (let i = 0; i < path.length; i++) {
      cur = this._child(cur, String(path[i]))
      if (cur == null) return undefined
    }
    return typeof cur === "string" ? cur : undefined
  }

  // Coalesce into one animation frame so highlighting runs AFTER the section
  // controllers have re-rendered their rows from the new plan.
  _scheduleApply() {
    if (this._applyRaf) cancelAnimationFrame(this._applyRaf)
    this._applyRaf = requestAnimationFrame(() => {
      this._applyRaf = null
      this._applyHighlights()
    })
  }

  // Clear stale marks, then tag each changed field that resolves to a live
  // input/textarea. Unresolved fields (section not mounted) are skipped and
  // re-tag next time this runs.
  _applyHighlights() {
    this._removeMarks()
    if (!this._changes || !this._changes.length) return
    this._changes.forEach((c, i) => {
      const el = this._resolveField(c.path)
      if (el) {
        el.classList.add("en-ai-changed")
        el.dataset.aiChangeIdx = String(i)
      }
    })
  }

  _removeMarks() {
    document.querySelectorAll(".en-ai-changed").forEach((el) => {
      el.classList.remove("en-ai-changed")
      delete el.dataset.aiChangeIdx
    })
  }

  // Map an exported path array to its editable DOM element. Covers plan-store
  // fields, generic/nested dynamic-lists, and the current-services /
  // unmet-needs custom renderers. Returns null when it can't resolve.
  _resolveField(path) {
    if (!Array.isArray(path) || !path.length) return null
    // Escape ONLY the chars that break a double-quoted attribute selector.
    // (CSS.escape is WRONG here — it would escape the literal dots in values
    //  like "dayInLife.morning" and stop them matching.)
    const a = (s) => String(s).replace(/["\\]/g, "\\$&")
    const q = (sel) => { try { return document.querySelector(sel) } catch (_) { return null } }

    // 1) plan-store field: the whole dotted path is the attribute value.
    let el = q(`[data-plan-field="${a(path.join("."))}"]`)
    if (el) return el

    if (path.length >= 3) {
      const field   = a(path[path.length - 1])
      const id      = a(path[path.length - 2])
      const section = a(path.slice(0, path.length - 2).join("."))
      const top     = String(path[0])

      // 2) dynamic-list (top-level or nested): section → row(by id) → field.
      el = q(`[data-dynamic-list-path-value="${section}"] [data-row][data-id="${id}"] [data-field="${field}"]`)
      if (el) return el
      const row = q(`[data-dynamic-list-path-value="${section}"] [data-row][data-id="${id}"]`)
      if (row) { const f = row.querySelector("textarea, input:not([type=checkbox]):not([type=radio])"); if (f) return f }

      // 3) custom renderers keyed by their own id attribute.
      if (top === "currentServices") { el = q(`[data-controller~="current-services-list"] input[data-cs-id="${id}"]`); if (el) return el }
      if (top === "unmetNeeds")      { el = q(`[data-controller~="unmet-needs-list"] [data-un-id="${id}"]`);           if (el) return el }
    }
    return null
  }

  // On a WHOLE-plan swap from someone else: new/sample/opened/restored plans
  // make the old highlights meaningless → drop them; otherwise the DOM was
  // rebuilt, so re-tag the marks.
  _onWholePlanChange(e) {
    if (!e || !e.detail || e.detail.path !== "*") return
    const src = e.detail.source
    if (src === "ai-proofread") return
    if (src === "newPlan" || src === "sample" || src === "sdp" || src === "revision" || src === "templatize") {
      this.clearHighlights()
      return
    }
    if (this._changes && this._changes.length) this._scheduleApply()
  }

  // Capture-phase document click: on a highlighted field → show its diff
  // popover; anywhere else (outside the popover) → dismiss.
  _onDocClickHandler(e) {
    const t = e.target
    if (this._pop && (t === this._pop || this._pop.contains(t))) return
    const field = t && t.closest ? t.closest(".en-ai-changed") : null
    if (field && field.dataset.aiChangeIdx != null) {
      const change = this._changes[Number(field.dataset.aiChangeIdx)]
      if (change) { this._showPopover(field, change); return }
    }
    this._hidePopover()
  }

  _showPopover(field, change) {
    const pop = this._ensurePopover()
    pop.querySelector(".en-ai-diff-body").innerHTML = diffHtml(change.old, change.new)
    pop.hidden = false
    // Measure, then place below the field (or above if it would overflow),
    // clamped to the viewport. position:fixed → viewport coordinates.
    const r  = field.getBoundingClientRect()
    const pw = pop.offsetWidth  || 360
    const ph = pop.offsetHeight || 120
    const left = Math.max(8, Math.min(r.left, window.innerWidth - pw - 8))
    let   top  = r.bottom + 6
    if (top + ph > window.innerHeight - 8 && r.top - ph - 6 > 8) top = r.top - ph - 6
    pop.style.left = `${left}px`
    pop.style.top  = `${top}px`
  }

  _hidePopover() { if (this._pop) this._pop.hidden = true }

  _ensurePopover() {
    if (this._pop) return this._pop
    const pop = document.createElement("div")
    pop.className = "en-ai-diff-pop"
    pop.hidden = true
    pop.innerHTML =
      '<div class="en-ai-diff-head">' +
        '<span class="en-ai-diff-title">AI change</span>' +
        '<span class="en-ai-diff-legend"><span class="en-ai-ins">added</span><span class="en-ai-del">removed</span></span>' +
        '<button type="button" class="en-ai-diff-close" aria-label="Close">&times;</button>' +
      '</div>' +
      '<div class="en-ai-diff-body"></div>'
    pop.querySelector(".en-ai-diff-close").addEventListener("click", () => this._hidePopover())
    document.body.appendChild(pop)
    this._pop = pop
    return pop
  }

  _reflectClearBtn() {
    if (this.hasClearBtnTarget) this.clearBtnTarget.hidden = !(this._changes && this._changes.length)
  }

  _loadHL() {
    try { const arr = JSON.parse(localStorage.getItem(HL_KEY) || "[]"); return Array.isArray(arr) ? arr : [] }
    catch (_) { return [] }
  }

  _saveHL(changes) {
    try {
      if (changes && changes.length) localStorage.setItem(HL_KEY, JSON.stringify(changes))
      else localStorage.removeItem(HL_KEY)
    } catch (_) {}
  }

  // ---- identity guard (don't import another participant's file) ----

  // A random, opaque id — no PHI. crypto.randomUUID where available.
  _newId() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID()
    return "p-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10)
  }

  // Get-or-create plan.meta.planId (mutates plan; caller persists).
  _ensurePlanId(plan) {
    if (!plan.meta || typeof plan.meta !== "object") plan.meta = {}
    if (!plan.meta.planId) plan.meta.planId = this._newId()
    return plan.meta.planId
  }

  // On editor load, make sure the current plan has a stable id, persisting it
  // ONCE (so we never rewrite storage on every connect).
  _ensureStablePlanId() {
    const plan = this._load()
    if (plan.meta && plan.meta.planId) return
    this._ensurePlanId(plan)
    savePlan(plan)
  }

  // Non-reversible fingerprint of the participant's identity (name + DOB) so a
  // file can be matched to a person WITHOUT carrying the name (keeps the export
  // PHI-free). Returns "" when no identity fields are set. FNV-1a → 8 hex.
  _participantHash(plan) {
    const c = (plan && plan.client) || {}
    const key = [c.firstName, c.lastName, c.dob].map(s => String(s || "").trim().toLowerCase()).join("|")
    if (!key.replace(/\|/g, "")) return ""
    let h = 0x811c9dc5
    for (let i = 0; i < key.length; i++) { h ^= key.charCodeAt(i); h = Math.imul(h, 0x01000193) }
    return (h >>> 0).toString(16).padStart(8, "0")
  }

  _participantName(plan) {
    const c = (plan && plan.client) || {}
    return [c.firstName, c.lastName].map(s => String(s || "").trim()).filter(Boolean).join(" ")
  }

  // Returns true to proceed. Prefers the planId match (exact same plan); falls
  // back to the participant fingerprint (same person) when a planId isn't on
  // both sides. Only prompts when we can POSITIVELY tell it's a different
  // plan/person — otherwise stays out of the way.
  _confirmSamePlan(doc, plan) {
    const curId   = plan && plan.meta && plan.meta.planId
    const curHash = this._participantHash(plan)
    let different = false
    if (doc.planId && curId)                    different = doc.planId !== curId
    else if (doc.participantHash && curHash)     different = doc.participantHash !== curHash
    if (!different) return true
    const name = this._participantName(plan) || "the plan open now"
    return window.confirm(
      `⚠ This proofread file was exported from a DIFFERENT plan than ${name}.\n\n` +
      "Importing it could overwrite the wrong participant's text.\n\n" +
      "Import anyway?"
    )
  }

  // ---- helpers ----
  _load() {
    try { return loadPlan() || {} } catch (_) { return {} }
  }

  _toast(msg) {
    let bar = document.getElementById("en-toast")
    if (!bar) {
      bar = document.createElement("div")
      bar.id = "en-toast"
      bar.style.cssText = "position:fixed;top:12px;right:12px;padding:10px 14px;border-radius:8px;font:600 13px/1.2 ui-sans-serif,system-ui;z-index:9999;box-shadow:0 8px 24px rgba(0,0,0,.18);background:#eef2ff;color:#3730a3;pointer-events:none;max-width:60vw;"
      document.body.appendChild(bar)
    }
    bar.textContent = msg
    bar.style.opacity = "1"
    clearTimeout(this._toastTimer)
    this._toastTimer = setTimeout(() => { bar.style.opacity = "0" }, 3200)
  }
}
